// Поверхности: карточки, шит, разделитель, плашка-объявление. SMY-66

import { useEffect, useRef, type ReactNode } from 'react'

import { noticeTone } from '@smyslika/core'
import { ICON_TITLES } from '@smyslika/ui-tokens'

import { Icon } from './Icon.tsx'

export function Card({
  kind = 'plain',
  selected,
  as = 'div',
  children,
  className,
  ...rest
}: {
  /** task — карточка задачи (радиус 18), nested — вложенная (14), dashed — «новый проект». */
  kind?: 'plain' | 'task' | 'nested' | 'dashed'
  selected?: boolean
  as?: 'div' | 'button'
  children?: ReactNode
  className?: string
} & Record<string, unknown>) {
  const Tag = as
  const classes = [
    'card',
    kind === 'plain' ? '' : `card--${kind}`,
    selected ? 'card--selected' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Tag className={classes} {...(as === 'button' ? { type: 'button' } : {})} {...rest}>
      {children}
    </Tag>
  )
}

export function Divider() {
  return <hr className="divider" />
}

/**
 * Плашка-объявление правила списания — экран 03б, — граница ответственности,
 * предложение. Вид — по тексту (`noticeTone` из ядра), а не по месту вызова:
 * плашка о коде из СМС приходит и живым событием, и строкой ленты после
 * перезагрузки, и вид у неё обязан быть один (SMY-551, решение владельца
 * 26.10.2026). Предупреждение — цветом и значком, без слова «Внимание».
 *
 * `live` — плашка пришла событием идущего запуска. Предупреждение тогда
 * объявляется (`role="alert"`) и встаёт на виду: о строках под код человек
 * узнаёт после ответа, а лента держит низ, и плашка над длинным ответом
 * оставалась бы выше экрана (ворота T6, ux-states-checker). Из ленты после
 * перезагрузки — `note`, без прокрутки: человек листает историю сам.
 */
export function Notice({ children, live = false, onShown }: { children: ReactNode; live?: boolean; onShown?: () => void }) {
  const warning = noticeTone(children) === 'warning'
  const shown = useRef<HTMLDivElement>(null)
  // Ссылкой, а не зависимостью эффекта: у вызова это новая функция каждого
  // рендера, а плашка встаёт на виду один раз.
  const announce = useRef(onShown)
  announce.current = onShown
  useEffect(() => {
    if (!warning || !live) return
    // Раньше эффекта ленты (эффект плашки идёт первым): лента узнаёт, что низ
    // держать не надо, до своей прокрутки к концу (шапка `scroll.ts`).
    announce.current?.()
    const frame = requestAnimationFrame(() => shown.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }))
    return () => cancelAnimationFrame(frame)
  }, [warning, live])
  if (warning) {
    return (
      <div ref={shown} className="notice notice--warning" role={live ? 'alert' : 'note'}>
        <span className="notice__icon">
          <Icon name="warning" size={18} title={ICON_TITLES.warning} />
        </span>
        <span>{children}</span>
      </div>
    )
  }
  return (
    <div className="notice">
      <span className="notice__dot" />
      <span>{children}</span>
    </div>
  )
}

export interface SheetProps {
  open: boolean
  onClose: () => void
  /** Читается программой чтения с экрана как имя окна. */
  label: string
  /** Показать «Esc» и крестик. Там, где закрытие — это отмена действия. */
  closable?: boolean
  children: ReactNode
}

/**
 * Подтверждение списания. На десктопе — окно 560px по центру, ниже
 * контрольной точки — нижний шит: тянуться к центру экрана большим пальцем
 * неудобно, а мышью к низу экрана — далеко. Разница только в раскладке,
 * содержимое одно.
 *
 * Закрывается по Esc и клику по фону, и оба равны отмене: правило экрана 04 —
 * отмена до старта не списывает ничего.
 */
export function Sheet({ open, onClose, label, closable, children }: SheetProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    // Фон не должен прокручиваться под окном: иначе колесо мыши уводит
    // страницу, а окно остаётся на месте.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="scrim"
      role="presentation"
      onClick={(e) => {
        // Только по самому фону: клик внутри окна закрывать его не должен.
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div className="sheet__grabber" />
        {closable === true ? (
          <div className="sheet__close">
            <span className="sheet__esc">Esc</span>
            <button type="button" className="sheet__x" onClick={onClose} aria-label="Закрыть">
              <Icon name="close" size={18} strokeWidth={2} />
            </button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}

/** Заголовок ленты: иконка сценария, название, всё прочее справа. */
export function FeedHeader({
  icon,
  title,
  children,
}: {
  icon?: Parameters<typeof Icon>[0]['name']
  title: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="feed-header">
      {icon ? <Icon name={icon} size={17} /> : null}
      <span>{title}</span>
      <span style={{ flex: 1 }} />
      {children}
    </div>
  )
}
